<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import { GripVertical } from '@lucide/vue'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  AdminStatusTag,
  RrInput,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,
  RrSpace,
  useDragReorder,
  type TableColumnType
} from '@xjlv2/admin-layout'
import type {
  CreateDictItemRequest,
  CreateDictTypeRequest,
  DictItem,
  DictType,
  UpdateDictItemRequest,
  UpdateDictTypeRequest
} from '../../types'

type TypeDraftMode = 'create' | 'edit'
type ItemDraftMode = 'create' | 'edit'

interface DictTypeDraft {
  mode: TypeDraftMode
  originalType: string
  name: string
  type: string
  sortOrder: number
  remark: string
}

interface DictItemDraft {
  mode: ItemDraftMode
  originalValue: string
  label: string
  value: string
  sortOrder: number
  remark: string
  enabled: boolean
}

const props = defineProps<{
  createApiClient: () => ApiClient
  dictTypes: DictType[]
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  canReadItems: boolean
  canCreateItem: boolean
  canUpdateItem: boolean
  canDeleteItem: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  create: [payload: CreateDictTypeRequest]
  update: [type: string, payload: UpdateDictTypeRequest]
  delete: [type: string]
}>()

const page = ref(1)
const pageSize = ref(20)

const showTypeDraftForm = ref(false)
const showItemDraftForm = ref(false)
const showItemDrawer = ref(false)
const activeDictType = ref('')
const activeItems = ref<DictItem[]>([])
const itemsLoading = ref(false)

const filters = ref({
  keyword: '',
  type: ''
})
const itemFilters = ref({
  value: '',
  label: ''
})
const selectedItemValues = ref<string[]>([])
const itemListRef = ref<HTMLElement | null>(null)
const drag = useDragReorder(itemListRef, {
  handle: '.drag-handle',
  dataIdAttr: 'data-item-value',
  disabled: () => !props.canUpdateItem || activeItems.value.length < 2,
  onReorder: (values) => void onReorderItems(values)
})

watch(
  [activeItems, () => props.canUpdateItem, itemsLoading, showItemDrawer],
  async () => {
    await nextTick()
    drag.sync()
  },
  { immediate: true, deep: true }
)
onBeforeUnmount(drag.destroy)

const typeDraft = ref<DictTypeDraft>(emptyTypeDraft())
const itemDraft = ref<DictItemDraft>(emptyItemDraft())

const filteredDictTypes = computed(() => {
  const keyword = filters.value.keyword.trim().toLowerCase()
  const type = filters.value.type.trim().toLowerCase()
  return props.dictTypes.filter((dictType) => {
    const matchesKeyword =
      !keyword ||
      dictType.name.toLowerCase().includes(keyword) ||
      dictType.type.toLowerCase().includes(keyword) ||
      dictType.remark.toLowerCase().includes(keyword)
    const matchesType = !type || dictType.type.toLowerCase().includes(type)
    return matchesKeyword && matchesType
  })
})

const paginatedDictTypes = computed(() => {
  const start = (page.value - 1) * pageSize.value
  return filteredDictTypes.value.slice(start, start + pageSize.value)
})

watch([() => filters.value.keyword, () => filters.value.type], () => {
  page.value = 1
})

const selectedDictType = computed(() =>
  props.dictTypes.find((dictType) => dictType.type === activeDictType.value) ?? null
)

const filteredActiveItems = computed(() => {
  const value = itemFilters.value.value.trim().toLowerCase()
  const label = itemFilters.value.label.trim().toLowerCase()
  return activeItems.value.filter((item) => {
    const matchesValue = !value || item.value.toLowerCase().includes(value)
    const matchesLabel = !label || item.label.toLowerCase().includes(label)
    return matchesValue && matchesLabel
  })
})

const filteredItemValues = computed(() => filteredActiveItems.value.map((item) => item.value))

watch(
  () => props.dictTypes,
  (dictTypes) => {
    if (!dictTypes.some((dictType) => dictType.type === activeDictType.value)) {
      if (showItemDrawer.value) {
        closeItemDrawer()
      }
      activeDictType.value = dictTypes[0]?.type ?? ''
    }
  }
)

async function loadItems(type: string) {
  if (!props.canReadItems || !type) {
    activeItems.value = []
    return
  }
  itemsLoading.value = true
  try {
    const api = props.createApiClient()
    activeItems.value = await api.get<DictItem[]>(`/api/saas/system/dicts/${encodeURIComponent(type)}/items`)
  } finally {
    itemsLoading.value = false
  }
}

watch(activeItems, (items) => {
  const currentValues = new Set(items.map((item) => item.value))
  selectedItemValues.value = selectedItemValues.value.filter((value) => currentValues.has(value))
})

function emptyTypeDraft(): DictTypeDraft {
  return {
    mode: 'create',
    originalType: '',
    name: '性别',
    type: 'gender',
    sortOrder: 10,
    remark: ''
  }
}

function emptyItemDraft(): DictItemDraft {
  return {
    mode: 'create',
    originalValue: '',
    label: '男',
    value: 'MALE',
    sortOrder: 10,
    remark: '',
    enabled: true
  }
}

function openCreateTypeForm() {
  typeDraft.value = emptyTypeDraft()
  showTypeDraftForm.value = true
}

function openEditTypeForm(dictType: DictType) {
  typeDraft.value = {
    mode: 'edit',
    originalType: dictType.type,
    name: dictType.name,
    type: dictType.type,
    sortOrder: dictType.sortOrder,
    remark: dictType.remark
  }
  showTypeDraftForm.value = true
}

function closeTypeDraftForm() {
  showTypeDraftForm.value = false
  typeDraft.value = emptyTypeDraft()
}

async function openItems(dictType: DictType) {
  if (!props.canReadItems) return
  activeDictType.value = dictType.type
  itemFilters.value = { value: '', label: '' }
  selectedItemValues.value = []
  showItemDrawer.value = true
  await loadItems(dictType.type)
}

function closeItemDrawer() {
  showItemDrawer.value = false
  closeItemDraftForm()
}

async function openCreateItemForm(dictType: DictType) {
  activeDictType.value = dictType.type
  itemDraft.value = emptyItemDraft()
  showItemDraftForm.value = true
  if (activeItems.value.length === 0) {
    await loadItems(dictType.type)
  }
}

function openEditItemForm(item: DictItem) {
  itemDraft.value = {
    mode: 'edit',
    originalValue: item.value,
    label: item.label,
    value: item.value,
    sortOrder: item.sortOrder,
    remark: item.remark,
    enabled: item.enabled
  }
  showItemDraftForm.value = true
}

function closeItemDraftForm() {
  showItemDraftForm.value = false
  itemDraft.value = emptyItemDraft()
}

function resetFilters() {
  filters.value = { keyword: '', type: '' }
}

function resetItemFilters() {
  itemFilters.value = { value: '', label: '' }
}

function submitTypeDraft() {
  if (!typeDraft.value.name.trim() || !typeDraft.value.type.trim()) return
  const payload = {
    name: typeDraft.value.name.trim(),
    sortOrder: Number(typeDraft.value.sortOrder),
    remark: typeDraft.value.remark.trim()
  }
  if (typeDraft.value.mode === 'create') {
    if (!props.canCreate) return
    emit('create', {
      type: typeDraft.value.type.trim(),
      ...payload
    })
  } else {
    if (!props.canUpdate) return
    emit('update', typeDraft.value.originalType, payload)
  }
  closeTypeDraftForm()
}

async function submitItemDraft() {
  if (!selectedDictType.value || !itemDraft.value.label.trim() || !itemDraft.value.value.trim()) return
  const payload = {
    label: itemDraft.value.label.trim(),
    sortOrder: Number(itemDraft.value.sortOrder),
    remark: itemDraft.value.remark.trim(),
    enabled: itemDraft.value.enabled
  }
  itemsLoading.value = true
  try {
    const api = props.createApiClient()
    if (itemDraft.value.mode === 'create') {
      if (!props.canCreateItem) return
      await api.post<DictItem>(`/api/saas/system/dicts/${encodeURIComponent(selectedDictType.value.type)}/items`, {
        value: itemDraft.value.value.trim(),
        ...payload
      })
    } else {
      if (!props.canUpdateItem) return
      await api.put<DictItem>(
        `/api/saas/system/dicts/${encodeURIComponent(selectedDictType.value.type)}/items/${encodeURIComponent(itemDraft.value.originalValue)}`,
        payload
      )
    }
    closeItemDraftForm()
    await loadItems(selectedDictType.value.type)
  } finally {
    itemsLoading.value = false
  }
}

function deleteType(dictType: DictType) {
  if (!props.canDelete || props.loading) return
  if (activeDictType.value === dictType.type) {
    closeItemDrawer()
  }
  emit('delete', dictType.type)
}

async function deleteItem(item: DictItem) {
  if (!props.canDeleteItem || !selectedDictType.value) return
  itemsLoading.value = true
  try {
    const api = props.createApiClient()
    await api.delete<void>(
      `/api/saas/system/dicts/${encodeURIComponent(selectedDictType.value.type)}/items/${encodeURIComponent(item.value)}`
    )
    await loadItems(selectedDictType.value.type)
  } finally {
    itemsLoading.value = false
  }
}

async function deleteSelectedItems() {
  if (!props.canDeleteItem || !selectedDictType.value || selectedItemValues.value.length === 0) return
  itemsLoading.value = true
  try {
    const api = props.createApiClient()
    for (const value of selectedItemValues.value) {
      await api.delete<void>(
        `/api/saas/system/dicts/${encodeURIComponent(selectedDictType.value.type)}/items/${encodeURIComponent(value)}`
      )
    }
    selectedItemValues.value = []
    await loadItems(selectedDictType.value.type)
  } finally {
    itemsLoading.value = false
  }
}

async function onReorderItems(values: string[]) {
  if (!selectedDictType.value || !props.canUpdateItem) return
  itemsLoading.value = true
  try {
    const api = props.createApiClient()
    await api.put<DictItem[]>(
      `/api/saas/system/dicts/${encodeURIComponent(selectedDictType.value.type)}/items/sort`,
      { values: mergeReorderedValues(values) }
    )
    await loadItems(selectedDictType.value.type)
  } finally {
    itemsLoading.value = false
  }
}

function formatTime(value: string) {
  if (!value) return ''
  return value.replace('T', ' ').replace(/\.\d+Z?$/, '')
}

function mergeReorderedValues(emitted: string[]): string[] {
  const queue = [...emitted]
  const visible = new Set(emitted)
  return activeItems.value.map((item) => (visible.has(item.value) ? queue.shift()! : item.value))
}

const typeColumns: TableColumnType<DictType>[] = [
  { key: 'name', title: '字典名称', minWidth: 160 },
  { key: 'type', title: '字典类型', minWidth: 140 },
  { key: 'sortOrder', title: '排序', width: 80 },
  { key: 'remark', title: '备注', minWidth: 140 },
  { key: 'createdAt', title: '创建时间', width: 160 },
  { key: 'actions', title: '操作', width: 180, align: 'right' }
]

const itemColumns: TableColumnType<DictItem>[] = [
  { key: 'drag', title: '', width: 40 },
  { key: 'label', title: '字典标签', minWidth: 140 },
  { key: 'value', title: '字典值', minWidth: 120 },
  { key: 'sortOrder', title: '排序', width: 80 },
  { key: 'status', title: '状态', width: 80 },
  { key: 'remark', title: '备注', minWidth: 120 },
  { key: 'actions', title: '操作', width: 120, align: 'right' }
]
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Dictionary Management</p>
        <h2>字典管理</h2>
      </div>
      <span>{{ filteredDictTypes.length }} / {{ dictTypes.length }} 个字典</span>
    </div>

    <AdminFilterBar :loading="loading" @reset="resetFilters">
      <RrInput v-model:value.trim="filters.keyword" autocomplete="off" placeholder="名称、类型、备注" />
      <RrInput v-model:value.trim="filters.type" autocomplete="off" placeholder="类型标识 (e.g. gender)" />
      <template #actions>
        <RrButton type="default" :disabled="loading" @click="resetFilters">重置</RrButton>
        <RrButton type="primary" :disabled="!canCreate || loading" @click="openCreateTypeForm">新增字典</RrButton>
      </template>
    </AdminFilterBar>

    <AdminDrawer
      v-model:open="showTypeDraftForm"
      :title="typeDraft.mode === 'create' ? '新增字典' : '修改字典'"
      :description="typeDraft.mode === 'create' ? (canCreate ? '可写' : '只读') : (canUpdate ? '可写' : '只读')"
      :width="500"
      @close="closeTypeDraftForm"
    >
      <RrForm
        layout="vertical"
        :disabled="loading || (typeDraft.mode === 'create' ? !canCreate : !canUpdate)"
        @submit.prevent="submitTypeDraft"
      >
        <RrFormItem label="字典名称" required>
          <RrInput v-model:value.trim="typeDraft.name" autocomplete="off" />
        </RrFormItem>
        <RrFormItem label="字典类型" required>
          <RrInput v-model:value.trim="typeDraft.type" autocomplete="off" :disabled="typeDraft.mode === 'edit'" />
        </RrFormItem>
        <RrFormItem label="排序">
          <RrInput v-model:value.number="typeDraft.sortOrder" type="number" min="0" max="9999" />
        </RrFormItem>
        <RrFormItem label="备注">
          <RrInput v-model:value.trim="typeDraft.remark" autocomplete="off" />
        </RrFormItem>
      </RrForm>
      <template #footer>
        <RrSpace>
          <RrButton @click="closeTypeDraftForm">取消</RrButton>
          <RrButton
            type="primary"
            :disabled="loading || (typeDraft.mode === 'create' ? !canCreate : !canUpdate)"
            @click="submitTypeDraft"
          >
            {{ typeDraft.mode === 'create' ? '创建字典' : '保存字典' }}
          </RrButton>
        </RrSpace>
      </template>
    </AdminDrawer>

    <AdminDataTable
      :columns="typeColumns"
      :data-source="paginatedDictTypes"
      :loading="loading"
      :current="page"
      :page-size="pageSize"
      :total="filteredDictTypes.length"
      row-key="type"
      :count="filteredDictTypes.length"
      :empty="filteredDictTypes.length === 0"
      empty-title="暂无匹配字典"
      @update:current="page = $event"
      @update:page-size="pageSize = $event"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'name'">
          <div>
            <strong>{{ record.name }}</strong>
            <small style="display: block; color: var(--rr-color-text-muted);">{{ record.type }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'type'">
          <code>{{ record.type }}</code>
        </template>
        <template v-else-if="column.key === 'sortOrder'">
          {{ record.sortOrder }}
        </template>
        <template v-else-if="column.key === 'remark'">
          {{ record.remark || '-' }}
        </template>
        <template v-else-if="column.key === 'createdAt'">
          {{ formatTime(record.createdAt) }}
        </template>
        <template v-else-if="column.key === 'actions'">
          <RrSpace size="small">
            <RrButton type="link" :disabled="!canReadItems || loading" @click="openItems(record)">
              字典配置
            </RrButton>
            <RrButton type="link" :disabled="!canUpdate || loading" @click="openEditTypeForm(record)">
              修改
            </RrButton>
            <RrButton type="link" danger :disabled="!canDelete || loading" @click="deleteType(record)">
              删除
            </RrButton>
          </RrSpace>
        </template>
      </template>
    </AdminDataTable>

    <!-- Dict Item Drawer -->
    <AdminDrawer
      v-model:open="showItemDrawer"
      :title="`字典配置 - ${selectedDictType?.type ?? ''}`"
      :description="`配置 ${selectedDictType?.name ?? ''} 的枚举字典项`"
      :width="720"
      @close="closeItemDrawer"
    >
      <div v-if="selectedDictType">
        <AdminFilterBar :loading="itemsLoading" @reset="resetItemFilters">
          <RrInput v-model:value.trim="itemFilters.value" autocomplete="off" placeholder="字典值" />
          <RrInput v-model:value.trim="itemFilters.label" autocomplete="off" placeholder="字典标签" />
          <template #actions>
            <RrButton type="default" :disabled="itemsLoading" @click="resetItemFilters">重置</RrButton>
            <RrButton
              type="primary"
              :disabled="!canCreateItem || itemsLoading"
              @click="openCreateItemForm(selectedDictType)"
            >
              新增项
            </RrButton>
            <RrButton
              v-if="selectedItemValues.length > 0"
              type="default"
              danger
              :disabled="!canDeleteItem || itemsLoading"
              @click="deleteSelectedItems"
            >
              批量删除 ({{ selectedItemValues.length }})
            </RrButton>
          </template>
        </AdminFilterBar>

        <AdminDrawer
          v-model:open="showItemDraftForm"
          :title="itemDraft.mode === 'create' ? '新增字典项' : '修改字典项'"
          :width="460"
          @close="closeItemDraftForm"
        >
          <RrForm
            layout="vertical"
            :disabled="itemsLoading || (itemDraft.mode === 'create' ? !canCreateItem : !canUpdateItem)"
            @submit.prevent="submitItemDraft"
          >
            <RrFormItem label="字典标签" required>
              <RrInput v-model:value.trim="itemDraft.label" autocomplete="off" />
            </RrFormItem>
            <RrFormItem label="字典值" required>
              <RrInput v-model:value.trim="itemDraft.value" autocomplete="off" :disabled="itemDraft.mode === 'edit'" />
            </RrFormItem>
            <RrFormItem label="排序">
              <RrInput v-model:value.number="itemDraft.sortOrder" type="number" min="0" max="9999" />
            </RrFormItem>
            <RrFormItem label="备注">
              <RrInput v-model:value.trim="itemDraft.remark" autocomplete="off" />
            </RrFormItem>
            <RrFormItem label="状态">
              <RrCheckbox v-model:checked="itemDraft.enabled">
                {{ itemDraft.enabled ? '启用' : '停用' }}
              </RrCheckbox>
            </RrFormItem>
          </RrForm>
          <template #footer>
            <RrSpace>
              <RrButton @click="closeItemDraftForm">取消</RrButton>
              <RrButton
                type="primary"
                :disabled="itemsLoading || (itemDraft.mode === 'create' ? !canCreateItem : !canUpdateItem)"
                @click="submitItemDraft"
              >
                {{ itemDraft.mode === 'create' ? '创建字典项' : '保存字典项' }}
              </RrButton>
            </RrSpace>
          </template>
        </AdminDrawer>

        <div ref="itemListRef" class="dict-item-table-container" style="margin-top: 16px;">
          <AdminDataTable
            :columns="itemColumns"
            :data-source="filteredActiveItems"
            :pagination="false"
            :loading="itemsLoading"
            row-key="value"
            :count="filteredActiveItems.length"
            :empty="filteredActiveItems.length === 0"
            empty-title="暂无字典项"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'drag'">
                <div class="drag-handle" :data-item-value="record.value" style="cursor: grab; display: flex; align-items: center; color: var(--rr-color-text-muted);">
                  <GripVertical :size="16" />
                </div>
              </template>
              <template v-else-if="column.key === 'label'">
                <strong>{{ record.label }}</strong>
              </template>
              <template v-else-if="column.key === 'value'">
                <code>{{ record.value }}</code>
              </template>
              <template v-else-if="column.key === 'sortOrder'">
                {{ record.sortOrder }}
              </template>
              <template v-else-if="column.key === 'status'">
                <AdminStatusTag :tone="record.enabled ? 'success' : 'neutral'">
                  {{ record.enabled ? '启用' : '停用' }}
                </AdminStatusTag>
              </template>
              <template v-else-if="column.key === 'remark'">
                {{ record.remark || '-' }}
              </template>
              <template v-else-if="column.key === 'actions'">
                <RrSpace size="small">
                  <RrButton type="link" :disabled="!canUpdateItem || loading" @click="openEditItemForm(record)">
                    修改
                  </RrButton>
                  <RrButton type="link" danger :disabled="!canDeleteItem || loading" @click="deleteItem(record)">
                    删除
                  </RrButton>
                </RrSpace>
              </template>
            </template>
          </AdminDataTable>
        </div>
      </div>
    </AdminDrawer>
  </section>
</template>
